import { jsPDF } from 'jspdf';
import { PlayerProfile, PlayerData, PlayerInjury, PlayerTransfer, PlayerAchievement, PlayerStats } from '../types/player';

// Helper function to convert meters to feet and inches
const metersToFeetAndInches = (heightStr: string | null | undefined): string => {
  if (!heightStr) {
    return 'N/A';
  }
  
  try {
    // Convert string like "1,90m" to number 1.90 by removing 'm' and replacing comma
    const m = parseFloat(heightStr.replace('m', '').replace(',', '.'));
    if (isNaN(m)) {
      return 'N/A';
    }
    const totalInches = m * 39.3701;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}"`;
  } catch (error) {
    console.error('Error converting height:', error);
    return 'N/A';
  }
};

// Helper function to safely get nested object value
const getNestedValue = (obj: any, path: string): string | undefined => {
  if (!obj) return undefined;
  
  const parts = path.split('.');
  const sectionId = parts[0]; // e.g., 'family', 'financial', etc.
  const property = parts[1]; // e.g., 'summary'
  
  // Check if the section exists in the object
  if (obj[sectionId]) {
    // For the exact format provided, we need to access the summary property
    if (property === 'summary') {
      // If summary is a string, return it directly
      if (typeof obj[sectionId].summary === 'string') {
        return obj[sectionId].summary;
      }
      // If summary is an object with a summary property, return that
      else if (obj[sectionId].summary && typeof obj[sectionId].summary === 'object') {
        // Check for summary.summary (nested structure)
        if ('summary' in obj[sectionId].summary && typeof obj[sectionId].summary.summary === 'string') {
          return obj[sectionId].summary.summary;
        }
        // Also check for other properties that might contain text
        else if ('text' in obj[sectionId].summary && typeof obj[sectionId].summary.text === 'string') {
          return obj[sectionId].summary.text;
        }
        // If there's a rating explanation, that's also useful text
        else if (obj[sectionId].summary.rating && 
                typeof obj[sectionId].summary.rating === 'object' && 
                'explanation' in obj[sectionId].summary.rating && 
                typeof obj[sectionId].summary.rating.explanation === 'string') {
          return obj[sectionId].summary.rating.explanation;
        }
      }
    }
  }
  
  return undefined;
};

// Define the sections to include in the PDF
const SECTIONS = [
  { id: 'family', title: 'Family', path: 'family.summary' },
  { id: 'financial', title: 'Financial', path: 'financial.summary' },
  { id: 'career', title: 'Career', path: 'career.summary' },
  { id: 'personal_life', title: 'Personal Life', path: 'personal_life.summary' },
  { id: 'agent', title: 'Agent Information', path: 'agent.summary' },
  { id: 'personality', title: 'Personality', path: 'personality.summary' },
  { id: 'early_career_and_education', title: 'Early Career & Education', path: 'early_career_and_education.summary' },
  { id: 'adverse_media', title: 'Adverse Media', path: 'adverse_media.summary' },
  { id: 'legal_and_criminal_history', title: 'Legal & Criminal History', path: 'legal_and_criminal_history.summary' },
  { id: 'sponsorships', title: 'Sponsorships', path: 'sponsorships.summary' }
] as const;

export const usePlayerPdfExport = () => {
  const handleExportPDF = (
    playerName: string,
    playerData: PlayerData | null,
    playerProfile: PlayerProfile,
    playerInjuries: PlayerInjury[] | null,
    playerTransfers: PlayerTransfer[] | null,
    playerAchievements: PlayerAchievement[] | null,
    playerStats: PlayerStats[] | null
  ) => {
    console.log('handleExportPDF called with:');
    console.log('- playerName:', playerName);
    console.log('- playerData:', playerData ? 'exists' : 'null');
    console.log('- playerProfile:', playerProfile ? 'exists' : 'null');
    console.log('- playerInjuries:', playerInjuries ? `${playerInjuries.length} items` : 'null');
    console.log('- playerTransfers:', playerTransfers ? `${playerTransfers.length} items` : 'null');
    console.log('- playerAchievements:', playerAchievements ? `${playerAchievements.length} items` : 'null');
    console.log('- playerStats:', playerStats ? `${playerStats.length} items` : 'null');
    
    if (!playerData) {
      console.error('Export failed: playerData is null or undefined');
      return;
    }
    
    if (!playerName) {
      console.error('Export failed: playerName is null or undefined');
      return;
    }
    
    if (!playerProfile) {
      console.error('Export failed: playerProfile is null or undefined');
      return;
    }

    console.log('All required data is present, generating PDF...');

    const doc = new jsPDF();
    let yPos = 20;
    const lineHeight = 10;
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxLineWidth = pageWidth - (margin * 2);

    // Add title
    doc.setFontSize(16);
    doc.text(`Player Report: ${playerName}`, margin, yPos);
    yPos += lineHeight * 2;
    
    // Add article references if available
    if (playerData.article_references && Object.keys(playerData.article_references).length > 0) {
      // Check if we need a new page
      if (yPos > doc.internal.pageSize.getHeight() - margin * 5) {
        doc.addPage();
        yPos = margin;
      }
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Referenced Articles', margin, yPos);
      yPos += lineHeight * 1.5;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      Object.entries(playerData.article_references).forEach(([title, url], index) => {
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        
        // Add article title
        doc.setFont(undefined, 'bold');
        const titleLines = doc.splitTextToSize(title, maxLineWidth - 10);
        doc.text(titleLines, margin + 5, yPos);
        yPos += lineHeight * titleLines.length;
        
        // Add URL with smaller font
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        const urlLines = doc.splitTextToSize(url, maxLineWidth - 10);
        doc.text(urlLines, margin + 5, yPos);
        yPos += lineHeight * urlLines.length + 5;
        
        doc.setFontSize(10);
      });
      
      yPos += lineHeight;
    }

    // Add Player Profile section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Player Profile', margin, yPos);
    yPos += lineHeight * 1.5;

    // Personal Information
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Personal Information', margin, yPos);
    yPos += lineHeight;

    doc.setFont(undefined, 'normal');
    const personalInfo = [
      `Full Name: ${playerProfile.full_name || 'Not available'}`,
      `Age: ${playerProfile.age || 'Not available'}`,
      `Date of Birth: ${playerProfile.date_of_birth || 'Not available'}`,
      `Place of Birth: ${playerProfile.place_of_birth_city || 'Unknown'}, ${playerProfile.place_of_birth_country || 'Unknown'}`,
      `Nationality: ${playerProfile.nationality || 'Not available'}`,
      `Height: ${playerProfile.height ? `${playerProfile.height.replace(',', '.')} (${metersToFeetAndInches(playerProfile.height)})` : 'Not available'}`,
      `Foot: ${playerProfile.foot || 'Not available'}`,
      ...(playerProfile.agent_name ? [`Agent: ${playerProfile.agent_name}`] : [])
    ];

    personalInfo.forEach(info => {
      doc.text(info, margin + 5, yPos);
      yPos += lineHeight;
    });
    yPos += lineHeight;

    // Club Information
    doc.setFont(undefined, 'bold');
    doc.text('Club Information', margin, yPos);
    yPos += lineHeight;

    doc.setFont(undefined, 'normal');
    const clubInfo = [
      `Current Club: ${playerProfile.club_name || 'Unknown'}`,
      `Position: ${playerProfile.position || 'Unknown'}`,
      `Other Positions: ${playerProfile.other_positions || 'N/A'}`,
      `Shirt Number: ${playerProfile.shirt_number || 'N/A'}`,
      `Joined On: ${playerProfile.joined_on || 'Unknown'}`,
      `Contract Until: ${playerProfile.contract_expires || 'Unknown'}`,
      `Market Value: ${playerProfile.market_value || 'Unknown'}`
    ];

    clubInfo.forEach(info => {
      doc.text(info, margin + 5, yPos);
      yPos += lineHeight;
    });
    yPos += lineHeight;

    // Injuries Information
    if (playerInjuries && playerInjuries.length > 0) {
      // Check if we need a new page
      if (yPos > doc.internal.pageSize.getHeight() - margin * 5) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont(undefined, 'bold');
      doc.text('Injury History', margin, yPos);
      yPos += lineHeight;

      doc.setFont(undefined, 'normal');
      playerInjuries.forEach(injury => {
        const fromDate = new Date(injury.from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const untilDate = injury.until_date 
          ? new Date(injury.until_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : 'Ongoing';
        const injuryInfo = `${injury.season}: ${injury.injury} (${fromDate} - ${untilDate}, ${injury.days} days, ${injury.games_missed} games missed)${!injury.until_date ? ' [ACTIVE]' : ''}`;
        
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.text(injuryInfo, margin + 5, yPos);
        yPos += lineHeight;
      });
    }
    yPos += lineHeight;
    
    // Transfers Information
    if (playerTransfers && playerTransfers.length > 0) {
      // Check if we need a new page
      if (yPos > doc.internal.pageSize.getHeight() - margin * 5) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont(undefined, 'bold');
      doc.text('Transfer History', margin, yPos);
      yPos += lineHeight;

      doc.setFont(undefined, 'normal');
      playerTransfers.forEach(transfer => {
        const transferDate = new Date(transfer.transfer_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const transferType = transfer.is_loan_end ? 'End of loan' : (transfer.is_loan ? 'Loan' : 'Transfer');
        const transferInfo = `${transfer.season}: ${transferType} from ${transfer.from_club_name} to ${transfer.to_club_name} (${transferDate}, Value: ${transfer.market_value}, Fee: ${transfer.fee})`;
        
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.text(transferInfo, margin + 5, yPos);
        yPos += lineHeight;
      });
    }
    yPos += lineHeight;
    
    // Achievements Information
    if (playerAchievements && playerAchievements.length > 0) {
      // Check if we need a new page
      if (yPos > doc.internal.pageSize.getHeight() - margin * 5) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont(undefined, 'bold');
      doc.text('Achievements', margin, yPos);
      yPos += lineHeight;

      // Sort achievements by season (newest first)
      const sortedAchievements = [...playerAchievements].sort((a, b) => {
        // Helper function to convert season to a sortable number
        const seasonToSortValue = (season: string): number => {
          // Handle 4-digit year format (e.g., "2009")
          if (/^\d{4}$/.test(season)) {
            return parseInt(season);
          }
          // Handle 2-digit/2-digit format (e.g., "23/24")
          else if (/^\d{2}\/\d{2}$/.test(season)) {
            const [startYear] = season.split('/');
            // Convert to full year (assuming 20xx for modern seasons)
            return 2000 + parseInt(startYear);
          }
          // Fallback for any other format
          return 0;
        };
        
        // Sort in descending order (newest seasons first)
        return seasonToSortValue(b.season_name) - seasonToSortValue(a.season_name);
      });

      doc.setFont(undefined, 'normal');
      sortedAchievements.forEach(achievement => {
        // Format the season for display
        let displaySeason = achievement.season_name;
        if (/^\d{2}\/\d{2}$/.test(achievement.season_name)) {
          const [startYear, endYear] = achievement.season_name.split('/');
          displaySeason = `20${startYear}/20${endYear}`;
        }
        
        const achievementInfo = `${displaySeason}: ${achievement.achievement_title} (${achievement.competition_name}, ${achievement.club_name})`;
        
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.text(achievementInfo, margin + 5, yPos);
        yPos += lineHeight;
      });
    }
    yPos += lineHeight;
    
    // Statistics Information
    if (playerStats && Array.isArray(playerStats) && playerStats.length > 0) {
      // Check if we need a new page
      if (yPos > doc.internal.pageSize.getHeight() - margin * 5) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont(undefined, 'bold');
      doc.text('Statistics', margin, yPos);
      yPos += lineHeight;

      // Add detailed stats table header
      yPos += lineHeight;
      doc.setFont(undefined, 'bold');
      doc.text('Competition', margin + 5, yPos);
      doc.text('Season', margin + 80, yPos);
      doc.text('Apps', margin + 130, yPos);
      doc.text('Goals', margin + 155, yPos);
      doc.text('Assists', margin + 180, yPos);
      yPos += lineHeight;
      
      // Add stats rows
      doc.setFont(undefined, 'normal');
      playerStats.forEach(stat => {
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
          
          // Repeat header on new page
          doc.setFont(undefined, 'bold');
          doc.text('Competition', margin + 5, yPos);
          doc.text('Season', margin + 80, yPos);
          doc.text('Apps', margin + 130, yPos);
          doc.text('Goals', margin + 155, yPos);
          doc.text('Assists', margin + 180, yPos);
          yPos += lineHeight;
          doc.setFont(undefined, 'normal');
        }
        
        doc.text(stat.competition_name, margin + 5, yPos);
        doc.text(stat.season_id, margin + 80, yPos);
        doc.text(stat.appearances.toString(), margin + 130, yPos);
        doc.text(stat.goals.toString(), margin + 155, yPos);
        doc.text(stat.assists.toString(), margin + 180, yPos);
        yPos += lineHeight;
      });
    }
    yPos += lineHeight;

    // Add horizontal line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos - lineHeight, pageWidth - margin, yPos - lineHeight);
    yPos += lineHeight;

    // Add Analysis Sections
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Player Analysis', margin, yPos);
    yPos += lineHeight * 2;

    // Add sections
    doc.setFontSize(12);
    SECTIONS.forEach((section) => {
      const sectionData = getNestedValue(playerData, section.path);
      if (sectionData) {
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.getHeight() - margin * 3) {
          doc.addPage();
          yPos = margin;
        }

        // Add section title
        doc.setFont(undefined, 'bold');
        doc.text(section.title, margin, yPos);
        yPos += lineHeight;

        // Add section content
        doc.setFont(undefined, 'normal');
        const lines = doc.splitTextToSize(sectionData, maxLineWidth);
        lines.forEach((line: string) => {
          if (yPos > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPos = margin;
          }
          doc.text(line, margin, yPos);
          yPos += lineHeight;
        });
        yPos += lineHeight; // Add space between sections
      }
    });

    try {
      console.log('Attempting to save PDF...');
      // Save the PDF
      doc.save(`${playerName.replace(/[^a-zA-Z0-9]/g, '_')}_report.pdf`);
      console.log('PDF saved successfully');
    } catch (error) {
      console.error('Error saving PDF:', error);
    }
  };

  return { handleExportPDF };
};